import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { generateSectionMenuItem, getOrderByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import {
  $virtualNodePayload,
  generateSectionFx,
  VirtualNodePayload,
} from '@nx-frontend/shared/store/virtual-portal-node';
import { PortalSectionMenuItem, VirtualPortalSection } from '@nx-frontend/shared/types';
import { ChangeEventType } from '@nx-frontend/shared/types';

interface SectionCreatePayload {
  title: string;
  openAfterCreate: boolean;
}

export const useSectionCreate = (): ((
  payload: SectionCreatePayload,
  pagePayload?: VirtualNodePayload
) => Promise<{ node: VirtualPortalSection; menuItem: PortalSectionMenuItem }>) => {
  const [virtualPagePayload, generateSection, getOrderById] = useUnit([
    $virtualNodePayload,
    generateSectionFx,
    getOrderByIdFx,
  ]);
  const setChanges = useChanges();
  const navigateMenuItem = useNavigateMenuItem();
  //const createFlexibleBlock = useCreateFlexibleBlock();

  return async ({ title, openAfterCreate }: SectionCreatePayload, pagePayloadParams?: VirtualNodePayload) => {
    const pagePayload = pagePayloadParams ?? virtualPagePayload;
    if (!pagePayload) {
      throw new Error('Missing node payload');
    }

    const menuType = pagePayload?.menuType;
    const parentId = pagePayload?.parentId;

    if (!menuType) {
      throw new Error('Missing node menu type');
    }

    const section = await generateSection({ title, menuType, parentId: parentId, visible: pagePayload.visible });
    const parentOrder = await getOrderById({ menuType, id: parentId ?? '' });
    const menuItem = generateSectionMenuItem({
      parentId: parentId,
      name: section.title,
      url: section.path,
      openInNewTab: false,
      index: parentOrder ? parentOrder?.length : 0,
      pageId: section.id,
      visible: pagePayload.visible,
    });

    const batchId = generateUuid();

    setChanges({
      type: ChangeEventType.ADD_NODE,
      batchId,
      oldValue: undefined,
      newValue: section,
    });

    setChanges({
      type: ChangeEventType.ADD_MENU_ITEM,
      batchId,
      oldValue: undefined,
      newValue: { menuType, item: menuItem },
    });

    // await createFlexibleBlock({
    //   place: 'page_body',
    //   template: { type: 'submenu', view: 'basic' },
    //   batchId,
    //   index: 0,
    //   removable: false,
    //   meta: {
    //     pageTitle: section.title,
    //     pageId: section.id,
    //     path: section.path,
    //   },
    // });

    if (openAfterCreate) {
      navigateMenuItem({ path: menuItem.url, originalPath: menuItem.originalUrl });
    }

    return {
      node: section,
      menuItem: menuItem,
    };
  };
};
